import type { Ref } from 'vue';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/vue-query';
import { BYPASS_PACKAGE_GATING } from '~/constants/package-gating';

type InsiderLevel = 'basic' | 'silver' | 'gold' | 'black';

export type TrainingLevel = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';

export type TrainingPresenterContacts = {
  email?: string;
  phone?: string;
  whatsapp?: string;
};

export type TrainingVideo = {
  id: string;
  slug: string;
  title: string;
  description: string;
  tags: string[];
  packageIds?: string[];
  packageNames?: string[];
  requiredLevel?: InsiderLevel;
  thumbnail: string;
  videoUrl: string;
  durationSec: number;
  createdAt: string;
  popularScore: number;
  viewCount?: number;
  lastViewedAt?: string | null;
  isWatched?: boolean;
  ctrButtonText?: string;
  ctrButtonLink?: string;
  projectIds?: string[];
  hasAccess?: boolean;
  // Phase 5 — Lesson Detail Modal additions
  presenterName?: string | null;
  presenterRole?: string | null;
  presenterAvatarUrl?: string | null;
  presenterContacts?: TrainingPresenterContacts | null;
  takeaways?: string[];
  level?: TrainingLevel | null;
  /** Insiders Academy module number — null for ordinary library content. */
  moduleOrder?: number | null;
};

type SortBy = 'recent' | 'popular' | 'level';

const WATCHED_KEY = 'training_watched_v1';
const PAGE_SIZE = 20;

export function useTraining() {
  const route = useRoute();
  const router = useRouter();

  const insiderLevel = useState<InsiderLevel>('insiderLevel', () => 'basic');
  const searchQuery = useState<string>('trainingSearch', () => '');
  const sortBy = useState<SortBy>('trainingSortBy', () => 'recent');
  const items: Ref<TrainingVideo[]> = useState<TrainingVideo[]>('trainingVideos', () => []);
  const total = useState<number>('trainingTotal', () => 0);
  const unlockedIds = useState<string[]>('trainingUnlockedIds', () => []);

  const watchedIds = useState<string[]>('trainingWatchedIds', () => {
    try {
      const raw = process.client ? localStorage.getItem(WATCHED_KEY) : null;
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch (e) {
      return [];
    }
  });

  function persistWatched() {
    if (process.client) localStorage.setItem(WATCHED_KEY, JSON.stringify(watchedIds.value));
  }

  const isUnlocked = (v: TrainingVideo) => {
    if (BYPASS_PACKAGE_GATING) {
      return true;
    }
    if (v.hasAccess !== undefined && v.hasAccess !== null) {
      return Boolean(v.hasAccess);
    }
    return unlockedIds.value.includes(v.id);
  };
  const isWatched = (id: string) => watchedIds.value.includes(id);

  const authStore = useAuthStore();

  async function fetchLevel() {
    if (!authStore.token) return;
    const res = await $fetch<{ level: InsiderLevel }>('/api/user/insider-level', {
      headers: {
        Authorization: `Bearer ${authStore.token}`,
      },
    });
    insiderLevel.value = res.level;
  }

  async function fetchUnlocked() {
    if (!authStore.token) return;
    try {
      const res = await $fetch<{ level: InsiderLevel; ids: string[] }>('/api/training/unlocked', {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });
      unlockedIds.value = res.ids;
    } catch (error) {
      console.error('Error fetching unlocked videos:', error);
      unlockedIds.value = [];
    }
  }

  const queryKey = computed(() => ['training-videos', searchQuery.value, sortBy.value, insiderLevel.value]);

  const {
    data: videosData,
    isLoading: isLoadingVideos,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchVideos,
  } = useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      // Public listing: works without auth so guests browsing /developer-vault
      // can see the catalogue. Endpoint returns the same shape; just the
      // video page itself is gated downstream.
      const res = await $fetch<{ items: TrainingVideo[]; total: number; page: number; pageSize: number }>(
        '/api/training/videos',
        {
          params: {
            q: searchQuery.value || undefined,
            sortBy: sortBy.value,
            level: insiderLevel.value,
            page: pageParam,
            pageSize: PAGE_SIZE,
          },
          headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {},
        },
      );
      return res;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((acc, page) => acc + page.items.length, 0);
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    enabled: () => !!insiderLevel.value && !!authStore.token,
  });

  watchEffect(() => {
    if (videosData.value) {
      items.value = videosData.value.pages.flatMap((page) => page.items);
      total.value = videosData.value.pages[0]?.total || 0;
    }
  });

  async function syncWatchedFromDatabase() {
    if (!authStore.token) return;

    try {
      const res = await $fetch<{ data: { watchedTrainings: Array<{ trainingId: string }> } }>('/api/trainings/stats', {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      const watchedFromDb = res.data.watchedTrainings.map((w) => w.trainingId);
      watchedIds.value = watchedFromDb;
      persistWatched();
    } catch (error) {
      console.error('Error syncing watched from database:', error);
    }
  }

  async function fetchVideos() {
    await refetchVideos();
  }

  async function refresh() {
    await fetchLevel();
    await fetchUnlocked();
    await fetchVideos();
    await syncWatchedFromDatabase();
  }

  async function markWatched(id: string) {
    if (!watchedIds.value.includes(id)) {
      watchedIds.value.push(id);
      persistWatched();
    }
    if (authStore.token) {
      $fetch(`/api/training/watch/${id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      }).catch(() => {});
    }
  }

  function gotoVideo(v: TrainingVideo) {
    // Guests see vault titles + thumbnails on /developer-vault (for SEO)
    // but cannot enter the actual video page. Redirect to signup with the
    // intended video as the post-auth landing target so they end up where
    // they tried to go.
    if (!authStore.token) {
      const target = encodeURIComponent(`/training/${v.slug}`);
      router.push(`/auth/register?redirect=${target}`);
      return;
    }
    router.push(`/training/${v.slug}`);
  }

  const watchedCount = computed(() => watchedIds.value.length);

  function getStatusInfo() {
    return {
      status: authStore.user?.status || 'Unknown',
      level: insiderLevel.value,
      userId: authStore.user?.id,
      user: authStore.user,
    };
  }

  return {
    insiderLevel,
    searchQuery,
    sortBy,
    items,
    total,
    unlockedIds,
    watchedIds,
    isUnlocked,
    isWatched,
    watchedCount,
    fetchLevel,
    fetchUnlocked,
    fetchVideos,
    refresh,
    markWatched,
    gotoVideo,
    getStatusInfo,
    isLoadingVideos,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
}
